import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/UI/Button';
import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';
import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';
import { AgentPlatformPageHeader } from '../../../components/AgentPlatform/AgentPlatformPageHeader';
import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';
import { AgentPlatformTableActionsCell } from '../../../components/AgentPlatform/AgentPlatformTableActionsCell';
import { StatusBadge } from '../../../components/AgentPlatform/StatusBadge';
import { ConfirmModal } from '../../../components/UI/ConfirmModal';
import { useToast } from '../../../components/UI/ToastProvider';
import { agentPlatformService } from '../../../services/agentPlatformService';
import type { AgentFlowDto, AutomationInstanceDto } from '../../../types/agentPlatform';
import { labelFlowStatus } from '../../../utils/agentPlatformLabels';
import { apTheme } from '../../../utils/agentPlatformTheme';

const DEFAULT_FLOW_DEFINITION = JSON.stringify(
  [
    {
      id: 'know-flow-tab',
      type: 'tab',
      label: 'Know Flow',
      disabled: false,
    },
  ],
  null,
  2
);

export const AgentFlowsListPage: React.FC = () => {
  const [flows, setFlows] = useState<AgentFlowDto[]>([]);
  const [instances, setInstances] = useState<AutomationInstanceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    automationInstanceId: '',
  });
  const [archiveTarget, setArchiveTarget] = useState<AgentFlowDto | null>(null);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [flowList, instList] = await Promise.all([
        agentPlatformService.getFlows(),
        agentPlatformService.getAutomationInstances(),
      ]);
      setFlows(flowList);
      setInstances(instList.filter(i => i.isActive));
      if (instList.length > 0 && !form.automationInstanceId) {
        const def = instList.find(i => i.isDefault) ?? instList[0];
        setForm(f => ({ ...f, automationInstanceId: def.id }));
      }
    } catch {
      addToast('Erro ao carregar fluxos.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const created = await agentPlatformService.createFlow({
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        automationInstanceId: form.automationInstanceId,
        definition: DEFAULT_FLOW_DEFINITION,
      });
      addToast('Fluxo criado.', 'success');
      navigate(`/agent-platform/flows/${created.id}/composer`);
    } catch {
      addToast('Erro ao criar fluxo.', 'error');
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      await agentPlatformService.archiveFlow(archiveTarget.id);
      addToast('Fluxo arquivado.', 'success');
      setArchiveTarget(null);
      load();
    } catch {
      addToast('Erro ao arquivar fluxo.', 'error');
    }
  };

  return (
    <AgentPlatformFeatureGate feature="flows">
      <AgentPlatformAdminGate>
        <AgentPlatformShell>
        <AgentPlatformPageHeader
          title="Fluxos de Trabalho"
          subtitle="Catálogo de automações com versionamento, editor visual e publicação controlada."
          area="workflow"
          actions={
            <>
              <Link to="/agent-platform/automation-instances">
                <Button variant="secondary">Instâncias</Button>
              </Link>
              <Button onClick={() => setShowCreate(true)}>Novo fluxo</Button>
            </>
          }
        />

        {showCreate && (
          <form
            onSubmit={handleCreate}
            className={`mb-6 ${apTheme.formPanel} sm:grid-cols-2`}
          >
            <input
              className={apTheme.input}
              placeholder="Código"
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              required
            />
            <input
              className={apTheme.input}
              placeholder="Nome"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              required
            />
            <select
              className={`${apTheme.input} sm:col-span-2`}
              value={form.automationInstanceId}
              onChange={e => setForm(f => ({ ...f, automationInstanceId: e.target.value }))}
              required
            >
              <option value="">Instância de Automação…</option>
              {instances.map(i => (
                <option key={i.id} value={i.id}>
                  {i.name} ({i.baseUrl})
                </option>
              ))}
            </select>
            <div className="sm:col-span-2 flex gap-2">
              <Button type="submit">Criar e abrir composer</Button>
              <Button type="button" variant="secondary" onClick={() => setShowCreate(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        )}

        <div className={apTheme.tableWrap}>
          <table className="min-w-full divide-y divide-slate-100">
            <thead>
              <tr className={apTheme.tableHead}>
                <th className="px-4 py-3 text-left">Nome</th>
                <th className="px-4 py-3 text-left">Código</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Versão</th>
                <th className={apTheme.tableActionsHead}>Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    Carregando…
                  </td>
                </tr>
              )}
              {flows.map(flow => (
                <tr key={flow.id} className="hover:bg-red-50/40 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{flow.name}</td>
                  <td className="px-4 py-3 text-sm font-mono text-slate-600">{flow.code}</td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={flow.status}
                      label={labelFlowStatus(flow.status)}
                      kind="flow"
                    />
                    {flow.isPublished && (
                      <span className="ml-2 text-xs text-green-700">publicado</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    v{flow.currentVersionNumber ?? '—'}
                  </td>
                  <AgentPlatformTableActionsCell>
                    <Link to={`/agent-platform/flows/${flow.id}/composer`} className="inline-flex">
                      <Button variant="secondary" size="sm">
                        Composer
                      </Button>
                    </Link>
                    <Button variant="danger" size="sm" onClick={() => setArchiveTarget(flow)}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </Button>
                  </AgentPlatformTableActionsCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ConfirmModal
          isOpen={!!archiveTarget}
          title="Arquivar fluxo"
          message={`Arquivar "${archiveTarget?.name}"?`}
          onConfirm={handleArchive}
          onCancel={() => setArchiveTarget(null)}
        />
        </AgentPlatformShell>
      </AgentPlatformAdminGate>
    </AgentPlatformFeatureGate>
  );
};
